/**
 * Liquid-first bundle UDF (simple | infinite | mixed).
 * Selection = fixed list (Liquid) + customer IO picks. Never the option catalog.
 */
(() => {
  const log = (event, detail = {}) => {
    try {
      console.info("[jafra-bundle-udf]", event, detail);
    } catch (e) {
      /* ignore */
    }
  };

  const parseJsonScript = (root, selector) => {
    const el = root?.querySelector?.(selector);
    if (!el?.textContent?.trim()) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  };

  const normalizeVariantId = (value) => {
    const id = String(value ?? "")
      .split("/")
      .pop();
    return /^\d+$/.test(id) ? id : "";
  };

  const normalizePriceCents = (raw) => {
    if (raw == null || raw === "") return null;
    const rawStr = String(raw).trim();
    const n = Number(rawStr);
    if (!Number.isFinite(n) || n <= 0) return null;

    // Dollar amounts use a decimal ("4.00", 4.5). Integers from Liquid/Shopify
    // JSON are already cents (400 = $4.00). Never multiply those by 100 —
    // that turned a $4 weight into $400 and skewed _UDF1 allocation.
    if (rawStr.includes(".")) {
      return Math.round(n * 100);
    }
    return Math.round(n);
  };

  const allocateWeightedCents = (totalCents, weights) => {
    const total = Math.round(Number(totalCents));
    const normalized = weights.map((w) => Math.max(Number(w) || 0, 0));
    const weightSum = normalized.reduce((sum, w) => sum + w, 0);
    if (total <= 0 || weightSum <= 0 || !normalized.length) return [];

    const raw = normalized.map((w) => (w / weightSum) * total);
    const allocated = raw.map((v) => Math.floor(v));
    let remainder = total - allocated.reduce((sum, v) => sum + v, 0);
    const fractions = raw
      .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
      .sort((a, b) => b.fraction - a.fraction || a.index - b.index);

    for (const entry of fractions) {
      if (remainder <= 0) break;
      allocated[entry.index] += 1;
      remainder -= 1;
    }
    return allocated;
  };

  const joinTitles = (titles) => titles.filter(Boolean).join(" <> ");
  const joinIds = (ids) => ids.filter(Boolean).join(" <> ");
  const joinPrices = (prices) => prices.filter((p) => p != null && p !== "").join(" <> ");
  const formatUdf1 = (centsList) =>
    centsList.map((cents) => (Number(cents) / 100).toFixed(2)).join("<>");

  /**
   * Product-specific workaround: Unstoppable Fragrance Duo + Sample (item 323885).
   * Theme allocation 43.49<>43.49<>4.02 is correct per expanded unit. Simple Bundles
   * appears to map delimited _UDF1 1:1 onto unique SKU lines (qty ignored), so a
   * 3-segment list mis-assigns the sample. Collapse to one unit price per unique
   * consecutive component (43.49<>4.02). Do not change other bundles.
   */
  const UDF1_UNIQUE_SKU_MAP_HANDLES = new Set([
    "new-jafra-unstoppable-fragrance-duo-sample-special-offers-s0826",
  ]);
  const UDF1_UNIQUE_SKU_MAP_SKUS = new Set(["323885"]);

  const isUnstoppableDuoSampleBundle = (form) => {
    const handle = String(form?.dataset?.productHandle || "").trim().toLowerCase();
    if (UDF1_UNIQUE_SKU_MAP_HANDLES.has(handle)) return true;
    const json = parseJsonScript(
      form?.closest?.(".product-form") || form || document,
      "[data-product-json]",
    );
    return (json?.variants || []).some((variant) =>
      UDF1_UNIQUE_SKU_MAP_SKUS.has(String(variant?.sku || "").trim()),
    );
  };

  const collapseUdf1ToUniqueConsecutiveComponents = (udf1, titles = []) => {
    const parts = String(udf1 || "")
      .split("<>")
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length < 2) return udf1;

    const collapsed = [];
    if (titles.length === parts.length) {
      let lastTitle = null;
      parts.forEach((part, index) => {
        const title = String(titles[index] || "")
          .trim()
          .toLowerCase();
        if (title && title === lastTitle) return;
        collapsed.push(part);
        lastTitle = title || lastTitle;
      });
    } else {
      parts.forEach((part) => {
        if (collapsed[collapsed.length - 1] !== part) collapsed.push(part);
      });
    }
    return collapsed.join("<>");
  };

  const applyUnstoppableDuoSampleUdf1Workaround = (form, udf1, titles = []) => {
    if (!udf1 || !isUnstoppableDuoSampleBundle(form)) return udf1;
    const next = collapseUdf1ToUniqueConsecutiveComponents(udf1, titles);
    if (next !== udf1) {
      log("udf1.unique_sku_map_workaround", { from: udf1, to: next });
    }
    return next;
  };

  const isConsultant = () =>
    window.customer?.userType === "Consultant" || window.customer?.isPartner === true;

  const orderType = () => {
    if (window.customer?.userType) return window.customer.userType;
    if (window.customer?.id) return "Retail";
    return "Guest";
  };

  const resolveForm = (root) =>
    root?.closest?.("form[action*='/cart/add']") ||
    root?.querySelector?.("form[action*='/cart/add']") ||
    document.querySelector("form[data-jafra-bundle-kind]") ||
    document.querySelector("form[action*='/cart/add']");

  const readKind = (form) =>
    String(form?.dataset?.jafraBundleKind || form?.querySelector?.('[name="properties[_jafra_bundle_kind]"]')?.value || "")
      .trim()
      .toLowerCase();

  const mergeLookup = (target, key, entry) => {
    const title = String(key || "").trim();
    if (!title || !entry?.id || !entry?.price) return;
    const existing = target[title];
    if (!existing) {
      target[title] = entry;
      return;
    }
    // Prefer higher retail weight. SB Infinite gift slots often use a stub
    // like $0.25 while catalog / Liquid has the real component retail.
    if ((entry.price || 0) > (existing.price || 0)) {
      target[title] = {
        ...existing,
        ...entry,
        id: entry.id || existing.id,
        price: entry.price,
      };
    }
  };

  const weightPriceCents = (item, fallbackCents = 0) => {
    const candidates = [
      normalizePriceCents(item?.price),
      normalizePriceCents(item?.compare_at_price ?? item?.compareAtPrice),
      normalizePriceCents(item?.retail_price ?? item?.retailPrice),
      normalizePriceCents(item?.variant_price ?? item?.variantPrice),
      fallbackCents || null,
    ].filter((n) => n != null && n > 0);
    return candidates.length ? Math.max(...candidates) : null;
  };

  const buildLookup = (form) => {
    const lookup = {};
    const fromLiquid = parseJsonScript(form, "[data-jafra-bundle-lookup]");
    if (fromLiquid && typeof fromLiquid === "object") {
      Object.entries(fromLiquid).forEach(([title, entry]) => {
        const id = normalizeVariantId(entry?.id);
        const price = normalizePriceCents(entry?.price);
        if (id && price) mergeLookup(lookup, title, { id, price });
      });
    }

    // SB app map: product_title + variant_options.value (shade labels).
    const sb2 = parseJsonScript(form.closest?.(".product-form") || document, "[data-jafra-sb-bundled-variants]") ||
      parseJsonScript(document, "[data-jafra-sb-bundled-variants]");
    if (sb2 && typeof sb2 === "object") {
      Object.values(sb2).forEach((items) => {
        const list = Array.isArray(items) ? items : [];
        list.forEach((item) => {
          if (!item || typeof item !== "object") return;
          const id = normalizeVariantId(item.variant_id ?? item.variantId ?? item.id);
          const productTitle = String(item.product_title ?? item.title ?? "").trim();
          const catalogFallback = productTitle ? lookup[productTitle]?.price || 0 : 0;
          const price = weightPriceCents(item, catalogFallback);
          if (!id || !price) return;
          const entry = { id, price };
          mergeLookup(lookup, productTitle, entry);
          const variantTitle = String(item.variant_title ?? "").trim();
          if (productTitle && variantTitle && !/^default title$/i.test(variantTitle)) {
            mergeLookup(lookup, `${productTitle} - ${variantTitle}`, entry);
          }
          (Array.isArray(item.variant_options) ? item.variant_options : []).forEach((option) => {
            mergeLookup(lookup, option?.value, entry);
          });
        });
      });
    }

    return lookup;
  };

  const readSbBundledVariants = (form) => {
    const root = form?.closest?.(".product-form") || form || document;
    return (
      parseJsonScript(root, "[data-jafra-sb-bundled-variants]") ||
      parseJsonScript(document, "[data-jafra-sb-bundled-variants]")
    );
  };

  /**
   * Expand SB2 Simple rows by quantity_in_bundle (Duo = one SKU, qty 2 → two segments).
   * Infinite option slots are customer picks, not fixed components.
   */
  const readSb2FixedExpanded = (form) => {
    const sb2 = readSbBundledVariants(form);
    if (!sb2 || typeof sb2 !== "object") return [];
    const out = [];
    Object.values(sb2).forEach((items) => {
      (Array.isArray(items) ? items : []).forEach((item) => {
        if (!item || typeof item !== "object") return;
        if (String(item.type || "") === "Infinite options") return;
        const title = String(item.product_title ?? item.title ?? "").trim();
        if (!title) return;
        const id = normalizeVariantId(item.variant_id ?? item.variantId ?? item.id);
        const price = weightPriceCents(item, 0);
        const qtyRaw = Number(item.quantity_in_bundle ?? item.quantity ?? 1);
        const qty = Number.isFinite(qtyRaw) && qtyRaw > 0 ? Math.floor(qtyRaw) : 1;
        for (let i = 0; i < qty; i += 1) {
          out.push({ title, id, price });
        }
      });
    });
    return out;
  };

  const readFixed = (form) => {
    const raw = parseJsonScript(form, "[data-jafra-bundle-fixed]");
    const fromLiquid = Array.isArray(raw)
      ? raw
          .map((item) => ({
            title: String(item?.title || "").trim(),
            id: normalizeVariantId(item?.id),
            price: normalizePriceCents(item?.price),
          }))
          .filter((item) => item.title)
      : [];
    const fromSb2 = readSb2FixedExpanded(form);
    // Prefer SB2 when it exposes more units (qty>1 / extra Simple components).
    if (fromSb2.length > fromLiquid.length) return fromSb2;
    return fromLiquid.length ? fromLiquid : fromSb2;
  };

  const readWeightPrices = (form) => {
    const raw = parseJsonScript(form, "[data-jafra-bundle-udf-weight-prices]");
    const fromScript =
      raw == null
        ? []
        : String(raw)
            .split(/<>|,/)
            .map((part) => part.trim())
            .filter(Boolean)
            .map((part) => normalizePriceCents(part))
            .filter(Boolean);
    const fromSb2 = readSb2FixedExpanded(form)
      .map((item) => item.price)
      .filter((n) => n != null && n > 0);
    if (fromSb2.length > fromScript.length) return fromSb2;
    return fromScript.length ? fromScript : fromSb2;
  };

  const isOptionSelect = (el) => {
    if (!el) return false;
    const name = String(el.name || "").toLowerCase();
    const id = String(el.id || "").toLowerCase();
    if (name === "id") return false;
    if (id === "product-select" || id.startsWith("product-select-")) return false;
    return true;
  };

  const readPicks = (form) => {
    const container =
      form?.querySelector?.("#simple-bundles-io-options") ||
      document.querySelector("#simple-bundles-io-options");
    if (!container) return [];
    return Array.from(container.querySelectorAll("select"))
      .filter(isOptionSelect)
      .map((select) => ({
        key: String(select.name || "").replace(/^properties\[(.+)\]$/, "$1") || select.id || "Choice",
        title: String(select.value || "").trim(),
      }))
      .filter((pick) => pick.title);
  };

  const resolveFromLookup = (lookup, title) => {
    const needle = String(title || "").trim();
    if (!needle || !lookup) return null;
    if (lookup[needle]) return lookup[needle];
    const lower = needle.toLowerCase();
    for (const [key, entry] of Object.entries(lookup)) {
      if (String(key).trim().toLowerCase() === lower) return entry;
    }
    let best = null;
    for (const [key, entry] of Object.entries(lookup)) {
      const keyLower = String(key).trim().toLowerCase();
      if (!keyLower || keyLower.length < 4) continue;
      if (lower.startsWith(`${keyLower} -`) || lower.startsWith(`${keyLower}–`)) {
        if (!best || keyLower.length > best.len) best = { entry, len: keyLower.length };
      }
    }
    return best?.entry || null;
  };

  /**
   * Product N first (by N), then Choice/Choose/Selection. Matches PDP select order
   * and what Simple Bundles expects for expand — not alphabetical ("CHOOSE" before "Product").
   */
  const sortPickKeys = (keys) =>
    [...keys].sort((a, b) => {
      const rank = (key) => {
        const product = String(key).trim().match(/^Product\s+(\d+)$/i);
        if (product) return [0, Number(product[1])];
        const choice = String(key)
          .trim()
          .match(/^(Choice|Choose|Selection|CHOOSE)\s+(\d+)/i);
        if (choice) return [1, Number(choice[2])];
        return [2, 0];
      };
      const [ra, na] = rank(a);
      const [rb, nb] = rank(b);
      return ra - rb || na - nb || String(a).localeCompare(String(b));
    });

  const buildSelection = (kind, fixed, picks, lookup) => {
    const titles = [];
    const ids = [];
    const prices = [];

    const pushTitle = (title) => {
      const resolved = resolveFromLookup(lookup, title);
      titles.push(title);
      if (resolved?.id) ids.push(String(resolved.id));
      if (resolved?.price) prices.push(String(resolved.price));
      else if (fixed.find?.((f) => f.title === title)?.price) {
        prices.push(String(fixed.find((f) => f.title === title).price));
        if (!resolved?.id && fixed.find((f) => f.title === title)?.id) {
          ids.push(String(fixed.find((f) => f.title === title).id));
        }
      }
    };

    if (kind === "simple") {
      fixed.forEach((item) => {
        if (!item.title) return;
        titles.push(item.title);
        if (item.id) ids.push(String(item.id));
        if (item.price) prices.push(String(item.price));
      });
    } else if (kind === "infinite") {
      picks.forEach((pick) => pushTitle(pick.title));
    } else if (kind === "mixed") {
      // Use UI select order (Product 1..N then CHOOSE), same as prod expand.
      // Do NOT rebuild from Liquid fixed list — metafield order often differs from Product N.
      if (picks.length) {
        picks.forEach((pick) => pushTitle(pick.title));
      } else {
        fixed.forEach((item) => {
          if (!item.title) return;
          titles.push(item.title);
          if (item.id) ids.push(String(item.id));
          if (item.price) prices.push(String(item.price));
        });
      }
    }

    return {
      titles,
      ids,
      prices,
      // simple: omit all selection props (SB expand from parent config)
      // mixed: titles only — prod expands with titles; stamped ids/wrong order broke expand to 2/4
      omitSelectionProps: kind === "simple",
      titlesOnly: kind === "mixed",
    };
  };

  const parentSaleCents = (form, params) => {
    const variantId = normalizeVariantId(params?.get?.("id") || form?.querySelector?.('[name="id"]')?.value);
    const meta = window.jafraAvalaraVariantMetadataCache?.[variantId];
    if (meta?.retailCents) return Math.round(Number(meta.retailCents));

    const priceEl = form?.closest?.(".product-form")?.querySelector?.("[data-product-price], [data-price]");
    const text = priceEl?.getAttribute?.("data-product-price") || priceEl?.textContent || "";
    const dollars = Number(String(text).replace(/[^0-9.]/g, ""));
    if (Number.isFinite(dollars) && dollars > 0) return Math.round(dollars * 100);

    const productJson = parseJsonScript(form.closest?.(".product-form") || document, "[data-product-json]");
    const variant = (productJson?.variants || []).find((v) => String(v.id) === variantId);
    if (variant?.price != null) return normalizePriceCents(variant.price) || Math.round(Number(variant.price));
    return 0;
  };

  const stampParams = (params, selection, udf1, kind) => {
    if (!params?.set) return;

    ["_bundle_selection", "_bundle_selection_ids", "_bundle_selection_prices", "_UDF1", "_UDF2"].forEach(
      (key) => params.delete(`properties[${key}]`),
    );

    if (!selection.omitSelectionProps && selection.titles.length) {
      params.set("properties[_bundle_selection]", joinTitles(selection.titles));
      if (!selection.titlesOnly) {
        if (selection.ids.length === selection.titles.length) {
          params.set("properties[_bundle_selection_ids]", joinIds(selection.ids));
        }
        if (selection.prices.length === selection.titles.length) {
          params.set("properties[_bundle_selection_prices]", joinPrices(selection.prices));
        }
      }
    }

    if (kind) params.set("properties[_jafra_bundle_kind]", kind);

    if (isConsultant()) {
      params.set("properties[_UDF3]", orderType());
      if (udf1) params.set("properties[_UDF1]", udf1);
    }
  };

  const stampForm = (form, selection, udf1, kind) => {
    const setProp = (key, value) => {
      let input = form.querySelector(`[name="properties[${key}]"]`);
      if (!value) {
        input?.remove();
        return;
      }
      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = `properties[${key}]`;
        form.appendChild(input);
      }
      input.value = value;
    };

    ["_bundle_selection", "_bundle_selection_ids", "_bundle_selection_prices", "_UDF2"].forEach((key) =>
      setProp(key, ""),
    );

    if (!selection.omitSelectionProps && selection.titles.length) {
      setProp("_bundle_selection", joinTitles(selection.titles));
      if (!selection.titlesOnly) {
        if (selection.ids.length === selection.titles.length) {
          setProp("_bundle_selection_ids", joinIds(selection.ids));
        }
        if (selection.prices.length === selection.titles.length) {
          setProp("_bundle_selection_prices", joinPrices(selection.prices));
        }
      }
    }

    if (kind) setProp("_jafra_bundle_kind", kind);
    if (isConsultant()) {
      setProp("_UDF3", orderType());
      setProp("_UDF1", udf1 || "");
    } else {
      setProp("_UDF1", "");
      setProp("_UDF3", "");
    }
  };

  const prepareFromRoot = (root, params = null, options = {}) => {
    const form = resolveForm(root);
    const kind = readKind(form);
    if (!kind || !["simple", "infinite", "mixed"].includes(kind)) {
      return { handled: false };
    }

    const lookup = buildLookup(form);
    const fixed = readFixed(form);
    const picks = readPicks(form);
    const selection = buildSelection(kind, fixed, picks, lookup);

    let weights = selection.prices.map((p) => Number(p)).filter((n) => n > 0);
    if (kind === "simple" && !weights.length) {
      weights = readWeightPrices(form);
    }

    const saleCents =
      Math.round(Number(options.parentPriceCents)) > 0
        ? Math.round(Number(options.parentPriceCents))
        : parentSaleCents(form, params);
    let udf1 = "";
    if (isConsultant() && saleCents > 0) {
      if (weights.length) {
        udf1 = formatUdf1(allocateWeightedCents(saleCents, weights));
      } else if (selection.titles.length > 1) {
        udf1 = formatUdf1(allocateWeightedCents(saleCents, selection.titles.map(() => 1)));
        log("udf1.equal_split_fallback", { kind, titles: selection.titles });
      } else if (selection.titles.length === 1) {
        // Single segment only when the selection truly has one unit — never stamp the
        // full parent sale for multi-component simple packs with missing weights.
        udf1 = (saleCents / 100).toFixed(2);
      }
    }
    udf1 = applyUnstoppableDuoSampleUdf1Workaround(form, udf1, selection.titles);

    log("prepare", {
      kind,
      titles: selection.titles,
      ids: selection.ids,
      prices: selection.prices,
      udf1,
      omitSelectionProps: selection.omitSelectionProps,
    });

    if (params) stampParams(params, selection, udf1, kind);
    if (params?.set) {
      picks.forEach((pick) => {
        if (!pick.key || !pick.title) return;
        params.set(`properties[${pick.key}]`, pick.title);
      });
    }
    if (form) {
      stampForm(form, selection, udf1, kind);
    }

    return {
      handled: true,
      kind,
      selection,
      udf1,
      choiceCount: picks.length,
      choiceKeys: picks.map((p) => p.key),
      hasBundleSelection: !selection.omitSelectionProps && selection.titles.length > 0,
      deferredUdf1: false,
    };
  };

  /** Prepare URLSearchParams cart add. Returns null if this form is not liquid-kind stamped. */
  const prepareCartAddSearchParams = (
    params,
    sourceElement,
    existingProperties = {},
    options = {},
  ) => {
    const form = resolveForm(sourceElement);
    const kind = readKind(form);
    if (!kind) {
      const propKind = String(existingProperties?._jafra_bundle_kind || "").toLowerCase();
      if (!propKind) return null;
    }
    const result = prepareFromRoot(sourceElement || form, params, options);
    if (!result.handled) return null;
    return result;
  };

  const prepareProductForm = (form, options = {}) => {
    const result = prepareFromRoot(form, null, options);
    return result.handled ? result : null;
  };

  /**
   * Brochure / off-PDP: read the same Liquid markers from fetched product HTML.
   * @param {Document|Element} doc
   * @param {object} properties existing line properties (may include Choice*)
   * @param {number} parentPriceCents
   */
  const prepareFromDocument = (doc, properties = {}, parentPriceCents = 0) => {
    const form =
      doc.querySelector?.("form[data-jafra-bundle-kind]") ||
      doc.querySelector?.("form[action*='/cart/add']");
    if (!form) return null;
    const kind = readKind(form);
    if (!kind) return null;

    const lookup = buildLookup(form);
    const fixed = readFixed(form);
    const picksFromProps = sortPickKeys(
      Object.keys(properties || {}).filter((key) =>
        /^(Product|Choice|Choose|Selection)\b/i.test(String(key).trim()),
      ),
    )
      .map((key) => ({ key, title: String(properties[key] || "").trim() }))
      .filter((p) => p.title);
    const picks = picksFromProps.length ? picksFromProps : readPicks(form);
    const selection = buildSelection(kind, fixed, picks, lookup);

    let weights = selection.prices.map((p) => Number(p)).filter((n) => n > 0);
    if (kind === "simple" && !weights.length) weights = readWeightPrices(form);

    let udf1 = "";
    const saleCents = Math.round(Number(parentPriceCents));
    if (isConsultant() && saleCents > 0) {
      if (weights.length) udf1 = formatUdf1(allocateWeightedCents(saleCents, weights));
      else if (selection.titles.length > 1) {
        udf1 = formatUdf1(allocateWeightedCents(saleCents, selection.titles.map(() => 1)));
      } else if (selection.titles.length === 1) {
        udf1 = (saleCents / 100).toFixed(2);
      }
    }
    udf1 = applyUnstoppableDuoSampleUdf1Workaround(form, udf1, selection.titles);

    const next = { ...properties };
    delete next._UDF2;
    if (selection.omitSelectionProps) {
      delete next._bundle_selection;
      delete next._bundle_selection_ids;
      delete next._bundle_selection_prices;
    } else if (selection.titles.length) {
      next._bundle_selection = joinTitles(selection.titles);
      if (selection.titlesOnly) {
        delete next._bundle_selection_ids;
        delete next._bundle_selection_prices;
      } else {
        if (selection.ids.length === selection.titles.length) {
          next._bundle_selection_ids = joinIds(selection.ids);
        }
        if (selection.prices.length === selection.titles.length) {
          next._bundle_selection_prices = joinPrices(selection.prices);
        }
      }
    }
    next._jafra_bundle_kind = kind;
    if (isConsultant()) {
      next._UDF3 = orderType();
      if (udf1) next._UDF1 = udf1;
    }

    return { handled: true, kind, properties: next, udf1, selection };
  };

  window.jafraBundleUdf = {
    prepareCartAddSearchParams,
    prepareProductForm,
    prepareFromDocument,
    readKind,
  };
})();
